import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LAVORI_CATEGORIES } from "@/data/content";
import Logo from "@/components/Logo";

const EASE = [0.16, 1, 0.3, 1];

const NavTabs = ({ activeTab, onNavigate }) => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobLavori, setMobLavori] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = activeTab === "home";
  const isLavori = activeTab.startsWith("lavori");
  // dark = light text (over hero). Only home-at-top is dark.
  const dark = isHome && !scrolled;
  const solid = !dark;

  const items = [
    { id: "home", label: t("tabs.home") },
    { id: "azienda", label: t("tabs.azienda") },
    { id: "risorse", label: t("tabs.risorse") },
    { id: "servizi", label: t("tabs.servizi") },
    { id: "lavori", label: t("tabs.lavori"), dropdown: true },
    { id: "certificazioni", label: t("tabs.certificazioni") },
    { id: "partner", label: t("tabs.partner") },
    { id: "contatti", label: t("tabs.contatti") },
    { id: "privacy", label: t("tabs.privacy") },
  ];

  const go = (tab) => {
    onNavigate(tab);
    setOpen(false);
    setDropOpen(false);
    setMobLavori(false);
  };

  const linkBase = "relative text-[0.8rem] font-semibold uppercase tracking-wide transition-colors duration-300";
  const linkColor = (active) =>
    active
      ? dark ? "text-gold" : "text-gold-2"
      : dark ? "text-cream/85 hover:text-cream" : "text-forest/75 hover:text-forest";

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        solid ? "bg-cream/90 backdrop-blur-xl border-b border-forest/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 h-28 flex items-center justify-between">
        <button onClick={() => go("home")} data-testid="nav-logo" className="flex items-center">
          <Logo tone={dark ? "light" : "dark"} />
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {items.map((it) =>
            it.dropdown ? (
              <div
                key={it.id}
                className="relative"
                onMouseEnter={() => setDropOpen(true)}
                onMouseLeave={() => setDropOpen(false)}
              >
                <button
                  onClick={() => go(`lavori:${LAVORI_CATEGORIES[0].id}`)}
                  data-testid="nav-tab-lavori"
                  className={`${linkBase} ${linkColor(isLavori)} flex items-center gap-1`}
                >
                  {it.label}
                  <ChevronDown size={14} className={`transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} />
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-300 ${isLavori ? "w-full" : "w-0"}`} />
                </button>
                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2, ease: EASE }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                      data-testid="lavori-dropdown"
                    >
                      <div className="bg-forest-deep text-cream rounded-sm shadow-2xl overflow-hidden min-w-[260px] py-2">
                        {LAVORI_CATEGORIES.map((c) => {
                          const active = activeTab === `lavori:${c.id}`;
                          return (
                            <button
                              key={c.id}
                              onClick={() => go(`lavori:${c.id}`)}
                              data-testid={`nav-lavori-${c.id}`}
                              className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors duration-200 flex items-center gap-3 ${
                                active ? "text-gold bg-forest-surface" : "text-cream/85 hover:text-gold hover:bg-forest-surface"
                              }`}
                            >
                              <span className="w-4 h-px bg-gold shrink-0" />
                              {c[lang]}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                key={it.id}
                onClick={() => go(it.id)}
                data-testid={`nav-tab-${it.id}`}
                className={`${linkBase} ${linkColor(activeTab === it.id)}`}
              >
                {it.label}
                <span className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-300 ${activeTab === it.id ? "w-full" : "w-0"}`} />
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className={`hidden sm:flex items-center rounded-full border overflow-hidden text-xs font-bold uppercase tracking-wider transition-colors duration-500 ${
              dark ? "border-cream/30" : "border-forest/20"
            }`}
            data-testid="lang-toggle"
          >
            <button
              onClick={() => lang !== "it" && toggle()}
              className={`px-3 py-1.5 transition-colors duration-300 ${
                lang === "it" ? "bg-gold text-forest-deep" : dark ? "text-cream/70 hover:text-cream" : "text-forest/60 hover:text-forest"
              }`}
              data-testid="lang-it"
            >
              IT
            </button>
            <button
              onClick={() => lang !== "en" && toggle()}
              className={`px-3 py-1.5 transition-colors duration-300 ${
                lang === "en" ? "bg-gold text-forest-deep" : dark ? "text-cream/70 hover:text-cream" : "text-forest/60 hover:text-forest"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          <button
            onClick={() => go("contatti")}
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center rounded-sm bg-gold text-forest-deep px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-gold-2 transition-colors duration-300"
          >
            {t("nav.cta")}
          </button>

          <button
            className={`lg:hidden p-2 transition-colors duration-300 ${dark ? "text-cream" : "text-forest"}`}
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest-deep text-cream flex flex-col lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="h-28 px-6 flex items-center justify-between border-b border-cream/10">
              <Logo tone="light" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close">
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto flex flex-col justify-center px-8 py-8 gap-1">
              {items.map((it, i) =>
                it.dropdown ? (
                  <div key={it.id}>
                    <button
                      onClick={() => setMobLavori((v) => !v)}
                      className="w-full flex items-center justify-between font-sans font-extrabold uppercase tracking-tight text-2xl py-2 text-cream hover:text-gold transition-colors"
                      data-testid="mobile-tab-lavori"
                    >
                      {it.label}
                      <ChevronDown size={22} className={`transition-transform ${mobLavori ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobLavori && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden pl-4"
                        >
                          {LAVORI_CATEGORIES.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => go(`lavori:${c.id}`)}
                              data-testid={`mobile-lavori-${c.id}`}
                              className="block w-full text-left py-2 text-cream/75 hover:text-gold text-base"
                            >
                              {c[lang]}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    key={it.id}
                    onClick={() => go(it.id)}
                    data-testid={`mobile-tab-${it.id}`}
                    className={`text-left font-sans font-extrabold uppercase tracking-tight text-2xl py-2 transition-colors ${
                      activeTab === it.id ? "text-gold" : "text-cream hover:text-gold"
                    }`}
                  >
                    {it.label}
                  </button>
                )
              )}
            </nav>
            <div className="p-8 border-t border-cream/10 flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-widest">
                <button onClick={() => lang !== "it" && toggle()} className={lang === "it" ? "text-gold" : "text-cream/50"}>IT</button>
                <span className="text-cream/30">/</span>
                <button onClick={() => lang !== "en" && toggle()} className={lang === "en" ? "text-gold" : "text-cream/50"}>EN</button>
              </div>
              <button onClick={() => go("contatti")} className="rounded-sm bg-gold text-forest-deep px-5 py-2.5 text-sm font-bold uppercase tracking-wider">
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default NavTabs;
